import { useSelector } from 'react-redux'
import {
  Navigate,
  Outlet,
  useLocation,
  useParams,
  useSearchParams
} from 'react-router-dom'

import {
  AuthenticationType,
  PublicSessionToken
} from 'authentication/shared/types/authentication.type'
import { HTTPClient } from 'infrastructure/shared/api'
import apiAsync from 'infrastructure/shared/api/api-async'
import { useLocales } from 'locales/hooks'
import { SitemodelUuid } from 'sitemodel/shared/types'

const PrivateRoutes = () => {
  const [searchParams] = useSearchParams()
  const { sitemodelUuid } = useParams<SitemodelUuid>()
  const location = useLocation()

  const { getSitemodelByLocale } = useLocales()

  const {
    token: tokenInState,
    logoutStatus,
    logoutRedirectOrigin
  } = useSelector((state: AuthenticationType) => state.authentication)

  const isLoggingOut = logoutStatus === 'logging_out'

  const tokenInURL = searchParams.get('token')

  const publicSessionToken: PublicSessionToken = window.publicSessionToken

  const token = tokenInURL ?? tokenInState ?? publicSessionToken

  const tokenType =
    !!token && token === publicSessionToken ? 'public' : 'private'

  const isEditInURL = location.pathname.includes('/edit')

  const isPublicTokenEditMode = isEditInURL && tokenType === 'public'

  const isAuthenticating = !token || isPublicTokenEditMode

  const unauthorizedSitemodelUuid = getSitemodelByLocale({
    demand: 'unauthorizedSitemodelUuid'
  })

  const isUnauthorizedPage =
    !!sitemodelUuid && sitemodelUuid === unauthorizedSitemodelUuid

  if (isLoggingOut) {
    let route = '/logged-out'
    if (logoutRedirectOrigin) {
      route += `?redirectOrigin=${encodeURIComponent(logoutRedirectOrigin)}`
    }
    return <Navigate to={route} replace state={{ from: location }} />
  }

  if (isAuthenticating) {
    /**
     * we use React Router's location parts instead of the simpler
     * window.location.href because otherwise there is a syncing problem
     * sometimes and the navigation changes redirectOrigin to unauthorized
     * sitemodel url before React Router renders the new route, making the
     * parameter useless
     */
    const redirectOrigin = `${location.pathname}${location.search}${location.hash}`

    return (
      <Navigate
        to={`${unauthorizedSitemodelUuid}?redirectOrigin=${encodeURIComponent(redirectOrigin)}`}
        replace
        state={{ from: location }}
      />
    )
  }

  if (!!token && tokenType === 'private' && isUnauthorizedPage) {
    return <Navigate to={'/'} replace state={{ from: location }} />
  }

  apiAsync.setAuthToken(token)
  HTTPClient.setToken(token)

  return <Outlet />
}

export default PrivateRoutes
